import { ajax } from "lib/observable";
import siteConfig from "site-config";
import { stringify } from "query-string";
import { updateEntities } from "redux-query";
import { Observable } from "rxjs";
import { formatMessage } from "lib/globalize-util";
import { getRecordsFromSchema, normalizeData } from "lib/api";
import { CollectionPOIs } from "schemas/collections";
import { add as snackbarAdd } from "actions/snackbars";
import {
    listCreate,
    listInsertOperation,
    listRemoveOperation,
    mapCreate,
    mixedCreate,
    MutableType,
} from "actions/mutate";
import { translateItemsList } from "helpers/product-collection";
import me from "models/me";
import {
    ROOT_NAMESPACE,
    ROOT_ID,
    ITEMS_NAMESPACE,
    ITEMS_ID,
    ITEM_DISPLAY_PREFERENCES_NAMESPACE,
    ITEM_DISPLAY_PREFERENCES_ID,
    ALLOCATION_DIMENSION_ID_PREFIX,
    ALLOCATION_DIMENSIONS_NAMESPACE,
    REVIEW_PLAN_ID_PREFIX,
    REVIEW_PLANS_NAMESPACE,
    DELIVERY_PURCHASE_ORDERS_ID,
    DELIVERY_PURCHASE_ORDERS_NAMESPACE,
    PoiTag,
    ALLOCATION_BAG_ID,
} from "constants/product-collection";
import { get, set } from "lodash";

import { COLLECTIONS, MUTATE_OPERATIONS_SENT } from "constants/action-types";
import { amphibiousGetIn } from "helpers/objUtils";
import { fromJS, List, Set } from "immutable";
import { mapToOperation } from "./helpers/mapToOperation";
import { getAndIncrementRequestId } from "./helpers/getAndIncrementRequestId";
import { POI_TAGS_OBJECT_ID_PATH } from "reducers/selectors/collection";
import { fetchCollectionPOIs } from "actions/collections/collection-pois";

export const fetchPOIsEpic = (action$, store) => {
    return action$.ofType(COLLECTIONS.COLLECTION.POIS.FETCH.START).switchMap(({ payload }) => {
        const { sessionId, collectionPOIs, merchantId } = store.getState().collection;
        const { collectionId, nextPage } = payload;
        const LIMIT = 25;
        const query = {
            sid: sessionId,
            merchant_id: merchantId || me.getLastMerchantId(),
            limit: LIMIT,
            min_poi_id: nextPage && collectionPOIs ? collectionPOIs.last() : undefined,
        };
        const url = `/v1/collections/${collectionId}/pois?${stringify(query)}`;
        return ajax({
            method: "GET",
            url: siteConfig.goAPI + url,
        })
            .map(({ response }) => {
                if (!response) {
                    return { results: [] };
                } else {
                    const records = getRecordsFromSchema(CollectionPOIs);
                    const data = normalizeData(response, CollectionPOIs, records);
                    return {
                        entities: data.entities,
                        results: data.result,
                    };
                }
            })
            .mergeMap(({ entities, results }: any) => [
                updateEntities({
                    collectionPOIs: (pois = {}) => {
                        return {
                            ...pois,
                            ...entities.collectionPOIs,
                        };
                    },
                }),
                {
                    payload: { results, hasMoreResults: LIMIT === results.length },
                    type: COLLECTIONS.COLLECTION.POIS.FETCH.STOP,
                },
            ])
            .catch((error) =>
                Observable.merge(
                    Observable.of({
                        payload: new Error(error),
                        error: true,
                        type: COLLECTIONS.COLLECTION.POIS.FETCH.STOP,
                    }),
                    Observable.of(snackbarAdd({ message: formatMessage("There was an error fetching history") })),
                ),
            );
    });
};

let fetchedPOIsSet = Set<string>();
export const fetchPIOEpic = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.POI.FETCH)
        .map(({ payload }) => payload)
        .flatMap(({ collectionId, collectionPoiId }) => {
            const { sessionId, merchantId } = store.getState().collection;
            const query = {
                sid: sessionId,
                merchant_id: merchantId,
            };

            if (fetchedPOIsSet.has(collectionPoiId)) {
                return [];
            }

            fetchedPOIsSet = fetchedPOIsSet.add(collectionPoiId);

            return ajax(
                `${siteConfig.goAPI}/v1/collections/${collectionId}/pois/${collectionPoiId}?${stringify(query)}`,
            )
                .map(({ response }) => {
                    if (!response) {
                        throw new Error(formatMessage("History entry not found"));
                    } else {
                        // HACK
                        const refs = get(response, "collection_poi.object_refs", []).filter((ref) => {
                            return !ref.object_id.includes("budget_item") && !ref.object_id.includes("asset_group");
                        });
                        set(response, "collection_poi.object_refs", refs);

                        const records = getRecordsFromSchema(CollectionPOIs);
                        const data = normalizeData(response, CollectionPOIs, records);

                        return {
                            entities: data.entities,
                        };
                    }
                })
                .mergeMap(({ entities }: any) => {
                    const mergedActions = [
                        updateEntities({
                            collectionPOIs: (pois = {}) => {
                                return {
                                    ...pois,
                                    ...entities.collectionPOIs,
                                };
                            },
                        }),
                    ];

                    const collectionPoi = entities.collectionPOIs[collectionPoiId];
                    collectionPoi.object_refs.forEach((ref) => {
                        mergedActions.push({
                            type: COLLECTIONS.COLLECTION.OBJECT.FETCH,
                            payload: {
                                collectionId,
                                collectionPoiId,
                                objectId: ref.object_id,
                                revision: ref.revision,
                            },
                        });
                    });

                    return mergedActions;
                })
                .catch(() => {
                    fetchedPOIsSet = fetchedPOIsSet.remove(collectionPoiId);
                    return Observable.merge(
                        Observable.of(snackbarAdd({ message: formatMessage("History entry not found") })),
                    );
                });
        });
};

export const fetchCollectionObject = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.OBJECT.FETCH)
        .map(({ payload }) => payload)
        .flatMap(({ collectionId, objectId, revision, collectionPoiId }) => {
            const { collection } = store.getState();
            const query = {
                sid: collection.sessionId,
                revision,
                with_snapshot: true,
                merchant_id: collection.merchantId,
            };
            return ajax(
                `${siteConfig.goAPI}/v1/collections/${collectionId}/collection_objects/${objectId}?${stringify(query)}`,
            )
                .map(({ response }) => response)
                .flatMap(({ object, ref }) => {
                    const mergedActions = [];

                    if (ref.type === MutableType.Map) {
                        if (ref.object_id === "budget_calculation_results") {
                            mergedActions.push(
                                mapCreate(
                                    "nuorder.service.collection.budget_calculation_results",
                                    `${ref.object_id}-${collectionPoiId}`,
                                    object.mutable_map.fields,
                                    ref,
                                ),
                            );
                        } else if (object.ref.object_id === ROOT_ID) {
                            // top-level document data (collection name, etc.)
                            mergedActions.push(
                                mapCreate(
                                    ROOT_NAMESPACE,
                                    `${ref.document_id}-${collectionPoiId}`,
                                    object.mutable_map.fields,
                                    ref,
                                ),
                            );
                        }
                    } else if (ref.type === MutableType.List) {
                        if (ref.object_id === ITEM_DISPLAY_PREFERENCES_ID) {
                            const items = translateItemsList(object.mutable_list.values);
                            mergedActions.push(
                                listCreate(
                                    ITEM_DISPLAY_PREFERENCES_NAMESPACE,
                                    { object_id: `${ref.object_id}-${collectionPoiId}` },
                                    items,
                                ),
                            );
                        } else if (ref.object_id === ITEMS_ID) {
                            // items
                            const items = translateItemsList(
                                object.mutable_list.values,
                                "struct_value.fields.id.string_value",
                            );
                            mergedActions.push(
                                listCreate(
                                    ITEMS_NAMESPACE,
                                    { object_id: `${ref.object_id}-${collectionPoiId}` },
                                    items,
                                ),
                            );
                        } else if (ref.object_id === DELIVERY_PURCHASE_ORDERS_ID) {
                            // delivery purchase orders
                            const items = translateItemsList(object.mutable_list.values);
                            mergedActions.push(
                                listCreate(
                                    DELIVERY_PURCHASE_ORDERS_NAMESPACE,
                                    { object_id: `${ref.object_id}-${collectionPoiId}` },
                                    items,
                                ),
                            );
                        }
                    } else if (ref.type === MutableType.Mixed) {
                        if (ref.object_id.includes(ALLOCATION_DIMENSION_ID_PREFIX)) {
                            mergedActions.push(
                                mixedCreate(
                                    ALLOCATION_DIMENSIONS_NAMESPACE,
                                    { object_id: `${ref.object_id}-${collectionPoiId}` },
                                    object.mutable_mixed.objects,
                                ),
                            );
                        } else if (ref.object_id.includes(REVIEW_PLAN_ID_PREFIX)) {
                            mergedActions.push(
                                mixedCreate(
                                    REVIEW_PLANS_NAMESPACE,
                                    { object_id: `${ref.object_id}-${collectionPoiId}` },
                                    object.mutable_mixed.objects,
                                ),
                            );
                        } else if (ref.object_id === ALLOCATION_BAG_ID) {
                            mergedActions.push(
                                mixedCreate(
                                    ALLOCATION_BAG_ID,
                                    { object_id: `${ref.object_id}-${collectionPoiId}` },
                                    object.mutable_mixed.objects,
                                ),
                            );
                        }
                    }

                    return mergedActions;
                })
                .catch(() => {
                    fetchedPOIsSet = fetchedPOIsSet.remove(collectionPoiId); // Remove from fetched POIs, to allow retries.
                    return Observable.merge(
                        Observable.of(snackbarAdd({ message: formatMessage("History entry not found") })),
                    );
                });
        });
};

export const tagCollectionWithVersionStatus = (
    action$,
    store, //TODO: Add UT.
) =>
    action$.ofType(COLLECTIONS.COLLECTION.POI.TAG.SET.START).flatMap(({ payload: { id, poiId } }) => {
        const { mutate, collection } = store.getState();
        const { collectionId, sessionId, merchantId } = collection;
        const poiTagsObjectId = amphibiousGetIn(mutate.snapshots, [collectionId].concat(POI_TAGS_OBJECT_ID_PATH));
        if (poiTagsObjectId) {
            const currentTags: List<any> = amphibiousGetIn(mutate.snapshots, [poiTagsObjectId]) || List();
            const setCompleteAction = { type: COLLECTIONS.COLLECTION.POI.TAG.SET.COMPLETE };
            const tagWithThatPoiIndex = currentTags.findIndex((curr) => curr.get(PoiTag.POI_ID) === poiId);
            const tagWithThatStatusIndex = currentTags.findIndex(
                (curr) => curr.get(PoiTag.COLLECTION_VERSION_STATUS_ID) === id,
            );

            const maxIndex = Math.max(tagWithThatPoiIndex, tagWithThatStatusIndex);
            const minIndex = Math.min(tagWithThatPoiIndex, tagWithThatStatusIndex);

            if (poiId) {
                const newTag = fromJS({ poi_id: poiId, collection_version_status_id: id });

                if (maxIndex >= 0 && minIndex >= 0) {
                    return [
                        listRemoveOperation(poiTagsObjectId, maxIndex),
                        listRemoveOperation(poiTagsObjectId, minIndex),
                        listInsertOperation(poiTagsObjectId, newTag, 0),
                        setCompleteAction,
                    ];
                }
                return [
                    ...(maxIndex === -1 ? [] : [listRemoveOperation(poiTagsObjectId, maxIndex)]),
                    listInsertOperation(poiTagsObjectId, newTag, 0),
                    setCompleteAction,
                ];
            } else {
                const url = `/v1/collections/${collectionId}/pois?sid=${sessionId}`;
                return ajax({
                    body: {
                        merchant_id: merchantId,
                    },
                    method: "POST",
                    url: siteConfig.goAPI + url,
                })
                    .flatMap(({ response }) => {
                        const newTag = fromJS({ poi_id: response.id, collection_version_status_id: id });
                        return [
                            ...(maxIndex === -1 ? [] : [listRemoveOperation(poiTagsObjectId, maxIndex)]),
                            listInsertOperation(poiTagsObjectId, newTag, 0),
                            setCompleteAction,
                            fetchCollectionPOIs(collectionId),
                        ];
                    })
                    .catch((error) => {
                        return Observable.merge(
                            Observable.of(
                                snackbarAdd({
                                    message: formatMessage("There was an error capturing bulks. Please try again."),
                                }),
                            ),
                        );
                    });
            }
        }
        return Observable.of(
            snackbarAdd({ message: formatMessage("There was an error capturing bulks. Please try again.") }),
        );
    });

export const untagCollectionWithVersionStatus = (
    action$,
    store, // TODO: Add UT
) =>
    action$.ofType(COLLECTIONS.COLLECTION.POI.TAG.REMOVE.START).flatMap(({ payload: { poiId } }) => {
        const { mutate, collection } = store.getState();
        const { collectionId } = collection;
        const poiObjectId = amphibiousGetIn(mutate.snapshots, [collectionId].concat(POI_TAGS_OBJECT_ID_PATH));
        if (poiObjectId) {
            const index = ((amphibiousGetIn(mutate.snapshots, [poiObjectId]) as List<any>) || List()).findIndex(
                (item) => item.get(PoiTag.POI_ID) === poiId,
            );
            return index >= 0
                ? [listRemoveOperation(poiObjectId, index), { type: COLLECTIONS.COLLECTION.POI.TAG.REMOVE.COMPLETE }]
                : Observable.of(
                      snackbarAdd({ message: formatMessage("There was an error capturing bulks. Please try again.") }),
                  );
        }
        return Observable.of(
            snackbarAdd({ message: formatMessage("There was an error capturing bulks. Please try again.") }),
        );
    });

export const syncTagWithCollection = (
    action$,
    store, // TODO: Add UT
) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.POI.TAG.SYNC.START)
        .filter(({ payload: { objectId } }) => {
            const { mutate } = store.getState();
            const { sentMutations, uncommittedMutations } = mutate;
            const sentMutation = sentMutations.get(objectId);
            const uncommittedMutation = uncommittedMutations.get(objectId) || List();
            return !sentMutation && !!uncommittedMutation.size;
        })
        .map(({ payload: { objectId } }: { payload: { objectId: string; operations?: any[] } }) => {
            const { collection, mutate } = store.getState();
            const { committedHEADs, uncommittedMutations } = mutate;
            const { sessionId, merchantId } = collection;

            const committedHEAD = committedHEADs.get(objectId);

            const operations = [];
            uncommittedMutations.get(objectId).forEach((mutation) => {
                mutation.operations.forEach((operation) => {
                    operations.push(mapToOperation(operation));
                });
            });
            const requestId = getAndIncrementRequestId(objectId);
            const revision = parseInt(committedHEAD.revision, 10) + 1;
            const command = {
                operations,
                revision,
                req_id: requestId,
            };

            store.dispatch({
                type: MUTATE_OPERATIONS_SENT,
                payload: {
                    requestId,
                    objectId,
                    revision,
                    objectType: committedHEAD.object_type,
                },
            });

            return { command, sessionId, requestId, collectionId: committedHEAD.document_id, merchantId };
        })
        .concatMap(({ command, requestId, sessionId, collectionId, merchantId }) => {
            const url = `/v1/collections/${collectionId}/poi_tags/mutate?sid=${sessionId}&req_id=${requestId}`;
            return ajax({
                body: { ...command, merchantId },
                method: "POST",
                url: siteConfig.goAPI + url,
            })
                .map(() => ({
                    payload: { collectionId },
                    type: COLLECTIONS.COLLECTION.POI.TAG.SYNC.COMPLETE,
                }))
                .catch((error) => {
                    return Observable.merge(
                        Observable.of(
                            snackbarAdd({
                                message: formatMessage("There was an error capturing bulks. Please try again."),
                            }),
                        ),
                    );
                });
        });
